import { useState } from 'react'
import './DataViewer.css'

/**
 * DataViewer — Renders data as a table in the left chat panel.
 *
 * Props:
 *   finding: { title, detail, type, data: array, sql: string, rowCount: number }
 *   onClose: function
 *   totalRows: number (optional — for data source preview)
 *   currentLimit: number (optional — current row limit)
 *   hasMore: bool (optional — more rows available)
 *   onChangeLimit: function(newLimit) (optional — fetch with new limit)
 */
export default function DataViewer({ finding, onClose, totalRows, currentLimit, hasMore, onChangeLimit }) {
  const [customLimit, setCustomLimit] = useState('')

  if (!finding || !finding.data || finding.data.length === 0) return null

  const rows = finding.data
  const columns = Object.keys(rows[0] || {})
  const maxRows = 200
  const displayRows = rows.slice(0, maxRows)
  const truncated = rows.length > maxRows

  const isDataSource = finding.type === 'data-source'

  // Format cell values
  const MONEY_COLS = /^(wage|salary|gross|net|amount|earnings|deduction|ytd|compensation)(s)?$|_(wage|salary|gross|net|amount|earnings|deduction|ytd|compensation)s?$/i
  const formatCell = (val, colName = '') => {
    if (val === null || val === undefined) return '—'
    // Embedded objects/arrays — show compact JSON, not [object Object]
    if (typeof val === 'object') {
      const s = JSON.stringify(val)
      if (s === '[]' || s === '{}' || s === 'null') return '—'
      return s.length > 60 ? s.substring(0, 57) + '…' : s
    }
    if (typeof val === 'number') {
      // ID columns — render as plain string, never add thousand separators
      if (/^id$|_id$|^id_/i.test(colName)) return String(val)
      // Only format as currency if the column name explicitly indicates money
      if (MONEY_COLS.test(colName)) {
        return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      // All other numbers: plain locale string (no $)
      return Number.isInteger(val) ? val.toLocaleString() : val.toFixed(4).replace(/\.?0+$/, '')
    }
    if (typeof val === 'string') {
      // Detect stringified JSON objects/arrays
      const trimmed = val.trim()
      if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 2) {
        try {
          const parsed = JSON.parse(trimmed)
          if (typeof parsed === 'object' && parsed !== null) {
            // Flatten simple objects to key=value pairs
            if (!Array.isArray(parsed)) {
              const entries = Object.entries(parsed).filter(([,v]) => v !== null && v !== undefined && v !== '')
              if (entries.length === 0) return '—'
              const flat = entries.map(([k,v]) => typeof v === 'object' ? `${k}=…` : `${k}=${v}`).join(', ')
              return flat.length > 80 ? flat.substring(0, 77) + '…' : flat
            }
            // Arrays: show count
            if (parsed.length === 0) return '—'
            return `[${parsed.length} items]`
          }
        } catch(e) { /* not JSON, fall through */ }
      }
      if (val.length > 80) return val.substring(0, 77) + '…'
    }
    return String(val)
  }

  // CSV download
  const handleDownload = () => {
    const header = columns.join(',')
    const csvRows = rows.map(row =>
      columns.map(col => {
        const val = row[col]
        if (val === null || val === undefined) return ''
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }).join(',')
    )
    const csv = [header, ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${finding.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'data'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleLimitSubmit = (e) => {
    e.preventDefault()
    const num = parseInt(customLimit, 10)
    if (num > 0 && onChangeLimit) {
      onChangeLimit(num)
      setCustomLimit('')
    }
  }

  return (
    <div className="data-viewer">
      <div className="dv-header">
        <div className="dv-header-left">
          {!isDataSource && (
            <span className={`finding-tag ${finding.type}`} style={{ marginBottom: 0 }}>
              {finding.type}
            </span>
          )}
          {isDataSource && (
            <span className="finding-tag" style={{ background: 'var(--blue-light)', color: 'var(--blue)', marginBottom: 0 }}>
              Data Source
            </span>
          )}
          <h3 className="dv-title">{finding.title}</h3>
          <p className="dv-subtitle">{finding.detail}</p>
        </div>
        <div className="dv-header-actions">
          <button className="dv-download-btn" onClick={handleDownload} title="Download as CSV">
            ⬇ CSV
          </button>
          <button className="dv-close" onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {finding.sql && (
        <div className="dv-sql">
          <span className="dv-sql-label">SQL</span>
          <code className="dv-sql-code">{finding.sql}</code>
        </div>
      )}

      <div className="dv-meta">
        <span>
          {rows.length} rows loaded
          {totalRows ? ` of ${totalRows.toLocaleString()} total` : ''}
          {' · '}{columns.length} columns
          {truncated && <span className="dv-truncated"> · showing first {maxRows}</span>}
        </span>
        {isDataSource && onChangeLimit && (
          <form className="dv-limit-form" onSubmit={handleLimitSubmit}>
            <label className="dv-limit-label">Rows:</label>
            <input
              type="number"
              className="dv-limit-input"
              placeholder={String(currentLimit || 100)}
              value={customLimit}
              onChange={e => setCustomLimit(e.target.value)}
              min="1"
              max="10000"
            />
            <button type="submit" className="dv-limit-btn">Load</button>
          </form>
        )}
      </div>

      <div className="dv-table-wrap">
        <table className="dv-table">
          <thead>
            <tr>
              <th className="dv-row-num">#</th>
              {columns.map(col => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr key={i}>
                <td className="dv-row-num">{i + 1}</td>
                {columns.map(col => (
                  <td key={col}>{formatCell(row[col], col)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
